// @ts-ignore - Element is a JS class, extended here for the new chart type.
import Element from "state/ui/editor/elements/Element";
import { cloneDeep } from "lib/lodash";

export type ChartType = "column" | "bar" | "line" | "pie" | "donut" | "area";

export interface ChartDataset {
  headers: string[];
  rows: Array<{
    label: string;
    values: number[];
  }>;
}

export interface ChartStyle {
  seriesColors: string[];
  fontFamily: string;
  fontId: string | null;
  fontSize: number;
  color: string;
  showLegend: boolean;
  showGridlines: boolean;
  showDataLabels: boolean;
  showAxes: boolean;
}

export interface ChartElementProps {
  type: "chart";
  chartType: ChartType;
  dataset: ChartDataset;
  style: ChartStyle;
  width: number;
  height: number;
  top: number;
  left: number;
  uniqueId?: string;
  restrictions?: string[];
  [key: string]: unknown;
}

class ChartElement extends (Element as unknown as {
  new (props: unknown): any;
}) {
  static RESTRICTIONS = [
    "removable",
    "visibility",
    "opacity",
    "angle",
    "duplicate",
    "sizeAndPosition",
    "color",
    "fontFamily",
    "fontSize",
    "animations",
    "chartDataEdit",
    "chartTypeChange"
  ];

  declare chartType: ChartType;
  declare dataset: ChartDataset;
  declare style: ChartStyle;
  declare restrictions: string[];
  declare uniqueId: string;

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(props: ChartElementProps) {
    super(props);
  }

  clone(newElementId?: string): ChartElement[] {
    const clonedElement = cloneDeep(this);
    clonedElement.uniqueId = newElementId || clonedElement.uniqueId;

    clonedElement.dataset = {
      headers: [...this.dataset.headers],
      rows: this.dataset.rows.map(row => ({
        label: row.label,
        values: [...row.values]
      }))
    };

    clonedElement.style = {
      ...this.style,
      seriesColors: [...this.style.seriesColors]
    };

    return [clonedElement];
  }

  updateAttributes(attributes: Record<string, unknown>): ChartElement {
    return super.updateAttributes(attributes);
  }

  getRestrictionsMap(): Record<string, boolean> {
    const restrictions: Record<string, boolean> = {};

    (this.constructor as typeof ChartElement).RESTRICTIONS.forEach(
      restriction => (restrictions[restriction] = false)
    );

    (this.restrictions as string[]).forEach(restriction => {
      restrictions[restriction] = true;
    });

    return restrictions;
  }
}

export default ChartElement;
